import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { toast } from "sonner";

export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [models, setModels] = useState([]);
  const [reqs, setReqs] = useState({ greenlit_requests: [], all_teasers: [], released_requests: [] });
  const [stats, setStats] = useState(null);
  const [modelSearch, setModelSearch] = useState("");
  const [modelForm, setModelForm] = useState({ name: "", bio: "", profile_photo: "", tags: "" });
  const [galleryForm, setGalleryForm] = useState({ model_id: "", title: "", cover_image: "", description: "", thumbnails: "", is_teaser: false });

  const load = () => {
    api.get("/admin/models").then(r => setModels(r.data));
    api.get("/admin/requests").then(r => setReqs(r.data));
    api.get("/admin/stats").then(r => setStats(r.data));
  };
  useEffect(() => { load(); }, []);

  const createModel = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/models", { ...modelForm, tags: modelForm.tags.split(",").map(s=>s.trim()).filter(Boolean) });
      toast.success("Model created");
      setModelForm({ name: "", bio: "", profile_photo: "", tags: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const createGallery = async (e) => {
    e.preventDefault();
    try {
      const thumbnails = galleryForm.thumbnails.split("\n").map(s=>s.trim()).filter(Boolean);
      await api.post("/admin/galleries", { ...galleryForm, thumbnails, hires_urls: thumbnails });
      toast.success("Gallery created");
      setGalleryForm({ model_id: "", title: "", cover_image: "", description: "", thumbnails: "", is_teaser: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const [releaseForm, setReleaseForm] = useState({ image_id: "", title: "", description: "", cover_image: "", thumbnails: "" });

  const releaseAgainstRequest = async (e) => {
    e.preventDefault();
    try {
      const thumbnails = releaseForm.thumbnails.split("\n").map(s=>s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/requests/release", { ...releaseForm, thumbnails, hires_urls: thumbnails });
      toast.success(`Released! Notified ${data.notified_users} voter(s).`);
      setReleaseForm({ image_id: "", title: "", description: "", cover_image: "", thumbnails: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const tabs = [["overview", "Overview"], ["models", "Models"], ["galleries", "Galleries"], ["greenlit", "Greenlit"], ["requests", "All Teasers"]];

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24">
        <div className="overline mb-3">Admin panel</div>
        <h1 className="font-serif-editorial text-5xl md:text-6xl tracking-tighter mb-6">Curator&apos;s desk.</h1>
        <div className="mb-10">
          <button onClick={()=>window.location.href="/admin/upload"} className="btn-white" data-testid="goto-upload">+ New gallery upload</button>
        </div>

        <div className="flex gap-8 border-b border-white/10 mb-10">
          {tabs.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`pb-4 text-xs font-mono-ui uppercase tracking-widest transition-colors ${tab === k ? "text-white border-b border-white -mb-px" : "text-neutral-500"}`}
              data-testid={`admin-tab-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="space-y-10" data-testid="admin-overview">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
              <StatBox label="Models" value={stats?.total_models ?? "…"} sub={stats?.hidden_models ? `${stats.hidden_models} hidden` : "all visible"} testId="stat-total-models"/>
              <StatBox label="Galleries" value={stats?.total_galleries ?? "…"} sub={stats?.draft_galleries ? `${stats.draft_galleries} drafts` : "all published"} testId="stat-total-galleries"/>
              <StatBox label="Images" value={stats?.total_images ?? "…"} sub="in archive"/>
              <StatBox label="Users" value={stats?.total_users ?? "…"} sub="registered"/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10">
              <StatBox label="Credits sold · this month" value={stats?.credits_sold_this_month ?? "…"} sub={`$${stats?.revenue_this_month ?? 0}`} testId="stat-credits-sold"/>
              <StatBox label="Greenlit — awaiting" value={stats?.pending_greenlit ?? "…"} sub="production queue" testId="stat-pending-greenlit"/>
              <StatBox label="Revenue · this month" value={`$${stats?.revenue_this_month ?? 0}`} sub={`${stats?.credits_sold_this_month ?? 0} credits`}/>
            </div>
            <div>
              <div className="overline mb-4">Quick actions</div>
              <div className="flex flex-wrap gap-3">
                <button onClick={()=>window.location.href="/admin/upload"} className="btn-white" data-testid="quick-upload">+ Upload new gallery</button>
                <button onClick={()=>setTab("models")} className="btn-ghost" data-testid="quick-model">+ Add new model</button>
                <button onClick={()=>setTab("greenlit")} className="btn-ghost" data-testid="quick-greenlit">View greenlit queue ({stats?.pending_greenlit ?? 0})</button>
              </div>
            </div>
          </div>
        )}


        {tab === "models" && (
          <div className="grid md:grid-cols-2 gap-12">
            <form onSubmit={createModel} className="space-y-4">
              <div className="overline">New model</div>
              <Field label="Name" value={modelForm.name} onChange={v => setModelForm({...modelForm, name: v})} testId="admin-model-name"/>
              <Field label="Bio" value={modelForm.bio} onChange={v => setModelForm({...modelForm, bio: v})} testId="admin-model-bio"/>
              <Field label="Profile photo URL" value={modelForm.profile_photo} onChange={v => setModelForm({...modelForm, profile_photo: v})} testId="admin-model-photo"/>
              <Field label="Tags (comma)" value={modelForm.tags} onChange={v => setModelForm({...modelForm, tags: v})} testId="admin-model-tags"/>
              <button type="submit" className="btn-white" data-testid="admin-create-model">Create model</button>
            </form>
            <div>
              <div className="overline mb-4">Existing ({models.length})</div>
              <input placeholder="Search models…" onChange={e => setModelSearch(e.target.value)}
                data-testid="model-search"
                className="w-full bg-transparent border border-white/20 px-3 py-2 text-sm mb-3 outline-none focus:border-white"/>
              <div className="border border-white/10 divide-y divide-white/10">
                {models.filter(m => m.name.toLowerCase().includes((modelSearch||"").toLowerCase())).map(m => (
                  <div key={m.model_id} className="p-4 flex items-center gap-4" data-testid={`model-row-${m.model_id}`}>
                    <img src={m.profile_photo} alt="" className={`w-12 h-12 object-cover ${m.hidden ? "opacity-40" : "grayscale"}`}/>
                    <div className="flex-1">
                      <div className="font-serif-editorial text-lg">{m.name} {m.hidden && <span className="ribbon !py-0.5 !px-1 !text-[9px] ml-2">HIDDEN</span>}</div>
                      <div className="text-xs text-neutral-500 font-mono-ui">{(m.tags||[]).join(" · ")}</div>
                    </div>
                    <button onClick={async ()=>{ await api.patch(`/admin/models/${m.model_id}/hide`, {hidden: !m.hidden}); load(); }}
                      className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`toggle-hide-${m.model_id}`}>
                      {m.hidden ? "Unhide" : "Hide"}
                    </button>
                    <button onClick={async ()=>{ if(window.confirm(`Soft-delete ${m.name}? Galleries preserved.`)){ await api.delete(`/admin/models/${m.model_id}`); load(); }}}
                      className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`delete-model-${m.model_id}`}>Delete</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "galleries" && (
          <div className="space-y-8">
            <div>
              <div className="overline mb-4">Published galleries</div>
              <div className="grid md:grid-cols-2 gap-3">
                {models.map(m => (
                  <GalleryEditList key={m.model_id} model={m}/>
                ))}
              </div>
            </div>
            <div className="border-t border-white/10 pt-8">
              <div className="overline mb-3">Legacy: quick create via URLs</div>
              <div className="text-sm text-neutral-500 mb-4">Prefer <a href="/admin/upload" className="text-white underline underline-offset-4">/admin/upload</a> for drag-and-drop uploads with auto-thumbnails.</div>
              <form onSubmit={createGallery} className="grid md:grid-cols-2 gap-8 max-w-4xl">
            <div className="space-y-4">
              <div className="overline">New gallery</div>
              <label className="block">
                <div className="text-xs text-neutral-500 font-mono-ui uppercase tracking-widest mb-2">Model</div>
                <select value={galleryForm.model_id} onChange={e => setGalleryForm({...galleryForm, model_id: e.target.value})}
                  className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white"
                  required data-testid="admin-gallery-model">
                  <option value="" className="bg-black">— select —</option>
                  {models.map(m => <option key={m.model_id} value={m.model_id} className="bg-black">{m.name}</option>)}
                </select>
              </label>
              <Field label="Title" value={galleryForm.title} onChange={v => setGalleryForm({...galleryForm, title: v})} testId="admin-gallery-title"/>
              <Field label="Cover URL" value={galleryForm.cover_image} onChange={v => setGalleryForm({...galleryForm, cover_image: v})} testId="admin-gallery-cover"/>
              <Field label="Description" value={galleryForm.description} onChange={v => setGalleryForm({...galleryForm, description: v})} testId="admin-gallery-desc"/>
            </div>
            <div className="space-y-4">
              <label className="block">
                <div className="text-xs text-neutral-500 font-mono-ui uppercase tracking-widest mb-2">Image URLs (one per line)</div>
                <textarea value={galleryForm.thumbnails} onChange={e => setGalleryForm({...galleryForm, thumbnails: e.target.value})}
                  rows={8}
                  className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-sm"
                  required data-testid="admin-gallery-urls"/>
              </label>
              <label className="flex items-center gap-3 text-sm text-neutral-400">
                <input type="checkbox" checked={galleryForm.is_teaser} onChange={e => setGalleryForm({...galleryForm, is_teaser: e.target.checked})} data-testid="admin-gallery-teaser"/>
                Publish as teaser (for voting page only)
              </label>
              <button type="submit" className="btn-white" data-testid="admin-create-gallery">Create gallery</button>
            </div>
          </form>
          </div>
          </div>
        )}

        {tab === "greenlit" && (
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="overline mb-4">Awaiting production ({reqs.greenlit_requests.length})</div>
              {reqs.greenlit_requests.length === 0 && <div className="text-neutral-500 text-sm">No greenlit requests yet.</div>}
              <div className="space-y-3">
                {reqs.greenlit_requests.map(r => {
                  const days = Math.floor((Date.now() - new Date(r.greenlit_at).getTime()) / 86400000);
                  return (
                  <div key={r.image_id} className="border border-white/10 p-4 flex gap-4" data-testid={`greenlit-${r.image_id}`}>
                    {r.url && <img src={r.url} alt="" className="w-24 h-32 object-cover"/>}
                    <div className="flex-1">
                      <div className="font-serif-editorial text-lg">{r.teaser_gallery_title}</div>
                      <div className="text-xs text-neutral-500 font-mono-ui mt-1">{r.model_name}</div>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <div className="ribbon">{r.via === "instant_commission" ? "COMMISSIONED" : `${r.votes} VOTES`}</div>
                        <div className="ribbon">{days === 0 ? "TODAY" : `${days}D PENDING`}</div>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button onClick={() => window.location.href = `/admin/upload?request=${r.image_id}`}
                          className="btn-white !py-2 !px-3 !text-[10px]" data-testid={`fulfill-${r.image_id}`}>
                          Fulfill → upload
                        </button>
                        <button onClick={() => setReleaseForm({ ...releaseForm, image_id: r.image_id })}
                          className="btn-ghost !py-2 !px-3 !text-[10px]"
                          data-testid={`select-greenlit-${r.image_id}`}>Quick release (URLs)</button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
              {reqs.released_requests?.length > 0 && (
                <>
                  <div className="overline mb-4 mt-10">Released ({reqs.released_requests.length})</div>
                  <div className="space-y-2">
                    {reqs.released_requests.map(r => (
                      <div key={r.image_id} className="border border-white/10 p-3 flex items-center gap-3 text-sm">
                        {r.url && <img src={r.url} alt="" className="w-10 h-10 object-cover"/>}
                        <div className="flex-1">
                          <div className="text-white">{r.teaser_gallery_title}</div>
                          <div className="text-xs text-neutral-500 font-mono-ui">Released {new Date(r.released_at).toLocaleDateString()}</div>
                        </div>
                        <div className="ribbon">DONE</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <form onSubmit={releaseAgainstRequest} className="space-y-4">
              <div className="overline">Release new gallery against request</div>
              <Field label="Request Image ID (select from left)" value={releaseForm.image_id} onChange={v => setReleaseForm({...releaseForm, image_id: v})} testId="release-image-id"/>
              <Field label="New gallery title" value={releaseForm.title} onChange={v => setReleaseForm({...releaseForm, title: v})} testId="release-title"/>
              <Field label="Cover URL" value={releaseForm.cover_image} onChange={v => setReleaseForm({...releaseForm, cover_image: v})} testId="release-cover"/>
              <Field label="Description" value={releaseForm.description} onChange={v => setReleaseForm({...releaseForm, description: v})} testId="release-desc"/>
              <label className="block">
                <div className="text-xs text-neutral-500 font-mono-ui uppercase tracking-widest mb-2">Image URLs (one per line)</div>
                <textarea value={releaseForm.thumbnails} onChange={e => setReleaseForm({...releaseForm, thumbnails: e.target.value})}
                  rows={6} required data-testid="release-urls"
                  className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white text-sm"/>
              </label>
              <button type="submit" className="btn-white" data-testid="release-submit">Release & notify voters</button>
            </form>
          </div>
        )}

        {tab === "requests" && (
          <div>
            <div className="overline mb-4">Teaser vote counts</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reqs.all_teasers.map(t => (
                <div key={t.image_id} className="border border-white/10 p-4 flex gap-4" data-testid={`admin-teaser-${t.image_id}`}>
                  <img src={t.url} alt="" className="w-24 h-24 object-cover"/>
                  <div className="flex-1">
                    <div className="font-serif-editorial text-lg">{t.gallery_title}</div>
                    <div className="text-xs text-neutral-500 font-mono-ui">{t.model_name}</div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className="progress-track flex-1"><div className="progress-fill" style={{width: `${Math.min(100, t.votes*10)}%`}}/></div>
                      <span className="text-xs font-mono-ui">{t.votes}/10</span>
                    </div>
                    {t.status === "greenlit" && <div className="ribbon mt-3">GREENLIT</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function Field({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 font-mono-ui uppercase tracking-widest mb-2">{label}</div>
      <input value={value} onChange={e => onChange(e.target.value)} required
        data-testid={testId}
        className="w-full bg-transparent border border-white/20 px-3 py-2 outline-none focus:border-white"/>
    </label>
  );
}

function StatBox({ label, value, sub, testId }) {
  return (
    <div className="bg-black p-6" data-testid={testId}>
      <div className="overline">{label}</div>
      <div className="font-serif-editorial text-4xl mt-3">{value}</div>
      {sub && <div className="text-xs font-mono-ui text-neutral-500 mt-2 uppercase tracking-widest">{sub}</div>}
    </div>
  );
}

function GalleryEditList({ model }) {
  const [gs, setGs] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [retagTo, setRetagTo] = useState("");
  const [allModels, setAllModels] = useState([]);
  useEffect(() => {
    api.get(`/models/${model.model_id}`).then(r => setGs(r.data.galleries || []));
    api.get("/models").then(r => setAllModels(r.data));
  }, [model.model_id]);
  const toggle = (id) => setSel(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action, extra = {}) => {
    if (sel.size === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${sel.size} galleries permanently?`)) return;
    try {
      await api.post("/admin/galleries/bulk", { action, gallery_ids: [...sel], ...extra });
      toast.success(`${action} · ${sel.size}`);
      setSel(new Set());
      const r = await api.get(`/models/${model.model_id}`);
      setGs(r.data.galleries || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="border border-white/10 p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-serif-editorial text-lg">{model.name}</div>
          <div className="text-xs text-neutral-500 font-mono-ui mt-1">{gs.length} galleries</div>
        </div>
        {sel.size > 0 && (
          <div className="flex gap-1 flex-wrap justify-end">
            <button onClick={() => bulk("publish")} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`bulk-publish-${model.model_id}`}>Publish {sel.size}</button>
            <button onClick={() => bulk("unpublish")} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`bulk-unpublish-${model.model_id}`}>Unpublish</button>
            <button onClick={() => bulk("delete")} className="btn-ghost !py-1 !px-2 !text-[9px]" data-testid={`bulk-delete-${model.model_id}`}>Delete</button>
            <select value={retagTo} onChange={e => { setRetagTo(e.target.value); if (e.target.value) bulk("retag", { new_model_id: e.target.value }); }}
              className="bg-transparent border border-white/20 text-[9px] px-1" data-testid={`bulk-retag-${model.model_id}`}>
              <option value="" className="bg-black">Re-tag to…</option>
              {allModels.filter(m => m.model_id !== model.model_id).map(m => <option key={m.model_id} value={m.model_id} className="bg-black">{m.name}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="space-y-2 mt-3">
        {gs.map(g => (
          <div key={g.gallery_id} className="flex items-center gap-2 text-sm border-t border-white/10 pt-2" data-testid={`gallery-row-${g.gallery_id}`}>
            <input type="checkbox" checked={sel.has(g.gallery_id)} onChange={() => toggle(g.gallery_id)} data-testid={`select-gallery-${g.gallery_id}`}/>
            <div className="flex-1 truncate">
              <div className="text-white">{g.title}</div>
              <div className="text-[10px] text-neutral-500 font-mono-ui">{(g.images||[]).length} images · {g.status}</div>
            </div>
            <a href={`/admin/upload?edit=${g.gallery_id}`} className="btn-ghost !py-1 !px-2 !text-[10px]" data-testid={`edit-gallery-${g.gallery_id}`}>Edit</a>
          </div>
        ))}
      </div>
    </div>
  );
}
